import React, { useEffect, useRef, useState } from 'react';

import { colors } from '../theme';

// Линейный график за период. Нарисован вручную на SVG: в проекте нет
// библиотеки графиков, а для четырёх рядов её ставить незачем.

// Ширина берётся из блока, в котором стоит график: он занимает всё место. Пока блок не
// измерен, рисуем по этой ширине.
const DEFAULT_W = 620;
const H = 300;
// Подписи дат по оси не ближе друг к другу, чем это (в пикселях).
const LABEL_GAP = 64;
const PAD = { top: 18, right: 18, bottom: 30, left: 40 };

const SERIES = [
  { key: 'sent', label: 'Отправлено', color: colors.accent },
  { key: 'delivered', label: 'Доставлено', color: colors.green },
  { key: 'read', label: 'Прочитано', color: colors.blue },
  { key: 'failed', label: 'Ошибки', color: colors.red, dashed: true },
];

// Верхняя линия сетки: круглое число, которое делится на 4 ровных шага
// (иначе на малых числах подписи выходили бы дробными).
const niceMax = (max) => {
  const rough = Math.max(max, 4) / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((s) => s >= rough);
  return Math.round(step * 4 * 100) / 100;
};

function PeriodChart(props) {
  const { data } = props;
  const hasData = Boolean(data && data.length > 0);
  const box = useRef(null);
  const [W, setW] = useState(DEFAULT_W);

  useEffect(() => {
    const node = box.current;
    if (!node) return undefined;
    const measure = () => setW(Math.max(280, Math.floor(node.clientWidth)));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasData]);

  if (!hasData) {
    return null;
  }

  const rawMax = Math.max(
    ...data.map((d) => Math.max(d.sent, d.delivered, d.read, d.failed))
  );
  const max = niceMax(rawMax);

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const x = (i) =>
    PAD.left + (data.length === 1 ? plotW / 2 : (plotW * i) / (data.length - 1));
  const y = (value) => PAD.top + plotH - (plotH * value) / max;

  // На длинных периодах подписи дней не помещаются: показываем каждую n-ю, чтобы между
  // ними оставалось место.
  const labelStep = Math.max(1, Math.ceil((LABEL_GAP * (data.length - 1)) / plotW));

  const ticks = [];
  const tickStep = max / 4;
  for (let v = 0; v <= max; v += tickStep) {
    ticks.push(v);
  }

  return (
    <div ref={box} style={{ width: '100%' }}>
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      style={{ display: 'block' }}
      role="img"
      aria-label="Статистика за период"
    >
      {ticks.map((v) => (
        <g key={v}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y(v)}
            y2={y(v)}
            stroke={colors.border}
            strokeWidth="1"
          />
          <text
            x={PAD.left - 8}
            y={y(v) + 4}
            textAnchor="end"
            fill={colors.textDim}
            fontSize="10"
          >
            {v}
          </text>
        </g>
      ))}

      {data.map((d, i) =>
        // Последняя подпись всегда есть; соседняя с ней не рисуется, чтобы они не слипались.
        i !== data.length - 1 &&
        (i % labelStep !== 0 || data.length - 1 - i < labelStep / 2) ? null : (
        <text
          key={d.date}
          x={x(i)}
          y={H - 10}
          textAnchor="middle"
          fill={colors.textDim}
          fontSize="10"
        >
          {d.date}
        </text>
        )
      )}

      {SERIES.map((s) => {
        const points = data.map((d, i) => `${x(i)},${y(d[s.key])}`).join(' ');
        return (
          <g key={s.key}>
            <polyline
              points={points}
              fill="none"
              stroke={s.color}
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeDasharray={s.dashed ? '6 5' : undefined}
            />
            {/* Точки только по краям — как на макете */}
            {!s.dashed &&
              [0, data.length - 1].map((i) => (
                <circle
                  key={i}
                  cx={x(i)}
                  cy={y(data[i][s.key])}
                  r="4"
                  fill={s.color}
                />
              ))}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

export { SERIES };
export default PeriodChart;
